import z from 'zod';
import { createModel, partySchema, refSchema, QueryMany, QueryOne } from '../../common';
import { dateSchema, nameSchema, objectIdSchema, phoneSchema } from '../../validation';
import { languageSchema } from '../../common/language.schema';
import { conversationChannelTypeSchema } from '../conversation/conversation.model';
import { UTagSharedSchema } from '../utag';
import {
  ticketCategorySchema,
  ticketPrioritySchema,
  ticketSourceSchema,
  ticketStatusSchema,
} from './ticket.enums';
import { ticketNoteSchema, ticketTimelineSchema } from './ticket.timeline.model';

/**
 * Who needs help.
 *
 * A ticket can be raised by someone Hero has no User row for — a driver
 * messaging the WhatsApp number from a phone nobody registered, a
 * dispatcher at a customer's office. So the contact details stand on
 * their own and `party` is optional: the ticket must be answerable
 * whether or not the person is in the database.
 */
export const ticketRequesterSchema = z.object({
  /** Party snapshot of the user, when the requester is a known account. */
  party: partySchema.optional(),
  name: nameSchema.optional(),
  phone: phoneSchema.optional(),
  email: z.string().trim().optional(),
  /** Language to answer in — the thread's, not a guess. */
  language: languageSchema.optional(),
});
export type TicketRequester = z.infer<typeof ticketRequesterSchema>;

/** The chat that raised the ticket, so staff can read what led to it. */
export const ticketConversationRefSchema = z.object({
  _id: objectIdSchema,
  channel: conversationChannelTypeSchema.optional(),
  /** WhatsApp phone / email address the thread runs on. */
  externalThreadId: z.string().trim().optional(),
});
export type TicketConversationRef = z.infer<typeof ticketConversationRefSchema>;

/** The order a ticket is about, when it is about one. */
export const ticketOrderRefSchema = z.object({
  _id: objectIdSchema,
  ref: refSchema.optional(),
});
export type TicketOrderRef = z.infer<typeof ticketOrderRefSchema>;

/**
 * `Ticket` collection — support requests, from anywhere.
 *
 * Every path that ends in "a human needs to look at this" lands here:
 * the AI escalating a WhatsApp thread, a dispatcher raising an issue
 * from the dashboard, an admin logging a phone call. One collection, so
 * "what is broken for our users right now" is one query rather than a
 * tour of three inboxes.
 *
 * NOT a `Lead`. A Lead is someone who might become a customer and is
 * owned by sales; a Ticket is someone who already is one (or is using
 * the product) and is stuck. They have different owners, different
 * lifecycles, and mixing them buries real problems under prospecting.
 *
 * Hero staff own tickets today — the requester's Org is recorded on
 * `org` for context and filtering, not to route the work.
 *
 * Operator-applied Mongo indexes (managed by the migration runner):
 * - `{ status: 1, createdAt: -1 }` — the inbox.
 * - `{ 'ref.short': 1 }` — quoting a number back over the phone.
 * - `{ 'requester.phone': 1 }` — "has this person written before?".
 * - `{ 'conversation._id': 1 }` — one ticket per escalated thread.
 */
export const TicketSchema = createModel({
  /** Human-readable ref (`K` prefix, e.g. `KCH021234`). Quoted to the user. */
  ref: refSchema,
  /** One line the inbox can be scanned by. */
  subject: nameSchema,
  /** What is actually wrong, in the requester's own words where possible. */
  description: z.string().trim().optional(),

  status: ticketStatusSchema,
  priority: ticketPrioritySchema,
  category: ticketCategorySchema,
  /** How it reached Hero. */
  source: ticketSourceSchema,

  /** Who needs the help. */
  requester: ticketRequesterSchema,
  /** The Org the requester belongs to, when known. Context, not routing. */
  org: partySchema.optional(),

  /** The chat this came out of — the transcript is the best context there is. */
  conversation: ticketConversationRefSchema.optional(),
  /** The order in question, when the ticket is about one. */
  order: ticketOrderRefSchema.optional(),

  /** Hero staff member handling it (AdminUser party snapshot). */
  assignedTo: partySchema.optional(),

  /** Correspondence + internal working notes, in one chronological thread. */
  notes: z.array(ticketNoteSchema).default([]),
  /** Append-only audit of every mutation. */
  timeline: z.array(ticketTimelineSchema).default([]),

  /** When it stopped needing an answer, and when it was filed away. */
  resolvedAt: dateSchema.optional(),
  closedAt: dateSchema.optional(),

  /** Universal tags (free-form `namespace:value` strings). */
  UTags: z.array(UTagSharedSchema).default([]),
});

export type Ticket = z.infer<typeof TicketSchema.model>;
export type TicketData = z.infer<typeof TicketSchema.data>;
export type TicketPartial = z.infer<typeof TicketSchema.partial>;
export type TicketQueryMany = QueryMany<Ticket>;
export type TicketQueryOne = QueryOne<Ticket>;
